import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { Alert, Avatar, Drawer, Spin, Tabs, Tag, Tooltip, Typography } from 'antd';
import { Database, KeyRound, ScrollText, Shield, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { TroubleshootingDrawer } from './UserDetailsDrawer/TroubleshootingDrawer';
import { ActivityLogsTab } from './UserDetailsDrawer/tabs/ActivityLogsTab';
import { AuthenticationTab } from './UserDetailsDrawer/tabs/AuthenticationTab';
import { AuthorizationTab } from './UserDetailsDrawer/tabs/AuthorizationTab';
import { AxiomLogsTab } from './UserDetailsDrawer/tabs/AxiomLogsTab';

const { Title, Text } = Typography;

export const Route = createFileRoute('/admin-console/user-access/users/view/$userId')({
    params: {
        parse({ userId }) {
            return { userId: BigInt(userId) };
        },
    },
    staticData: {
        presentation: 'overlay',
    },
    component: ViewUserRoute,
});

function ViewUserRoute() {
    const { userId } = Route.useParams();
    const { trpc } = useTrpc();
    const navigate = useNavigate();
    const [open, setOpen] = useState(true);
    const [troubleshootingOpen, setTroubleshootingOpen] = useState(false);

    const { data, isLoading, error } = trpc.admin.user.getUserComprehensiveDetails.useQuery(
        { userId },
        {
            retry: false,
        },
    );

    // Determine user status
    const getUserStatus = () => {
        if (!data) return null;

        if (data.auth0?.exists === false) return { type: 'warning', text: 'Not in Auth0' };
        if (data.inviteStatus.hasPendingInvite && !data.inviteStatus.inviteExpired)
            return { type: 'info', text: 'Invited' };
        if (data.inviteStatus.inviteExpired) return { type: 'error', text: 'Invite Expired' };
        if (!data.authentication.provider) return { type: 'warning', text: 'Not Registered' };
        if (!data.user.isEmailVerified) return { type: 'warning', text: 'Email Unverified' };
        if (data.auth0?.user?.blocked) return { type: 'error', text: 'Blocked' };
        if (data.user.lastLoginAt) return { type: 'success', text: 'Active' };
        return { type: 'default', text: 'Registered' };
    };

    const userStatus = getUserStatus();

    const tabItems = [
        {
            key: 'authentication',
            label: (
                <span className="flex items-center gap-2">
                    <KeyRound className="h-4 w-4" />
                    Authentication
                </span>
            ),
            children: data && <AuthenticationTab data={data} />,
        },
        {
            key: 'authorization',
            label: (
                <span className="flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Authorization
                </span>
            ),
            children: data && <AuthorizationTab data={data} />,
        },
        {
            key: 'logs',
            label: (
                <span className="flex items-center gap-2">
                    <ScrollText className="h-4 w-4" />
                    Activity & Logs
                </span>
            ),
            children: data && <ActivityLogsTab data={data} />,
        },
        {
            key: 'axiom-logs',
            label: (
                <span className="flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    Axiom Logs
                </span>
            ),
            children: data && <AxiomLogsTab data={data} />,
        },
    ];

    return (
        <PageController route={Route}>
            <Drawer
                title={
                    <div className="flex items-center gap-4">
                        {data && (
                            <>
                                <Avatar size={48} src={data.user.photoUrl}>
                                    {!data.user.photoUrl &&
                                        (data.user.firstName?.[0] || data.user.email[0]).toUpperCase()}
                                </Avatar>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <Title level={4} className="mb-0">
                                            {data.user.firstName} {data.user.lastName}
                                        </Title>
                                        {userStatus && (
                                            <Tag
                                                variant="outlined"
                                                color={
                                                    userStatus.type === 'success'
                                                        ? 'green'
                                                        : userStatus.type === 'warning'
                                                          ? 'orange'
                                                          : userStatus.type === 'error'
                                                            ? 'red'
                                                            : userStatus.type === 'info'
                                                              ? 'blue'
                                                              : 'default'
                                                }
                                            >
                                                {userStatus.text}
                                            </Tag>
                                        )}
                                        {data.user.isAdmin && (
                                            <Tag variant="outlined" color="gold">
                                                Admin
                                            </Tag>
                                        )}
                                    </div>
                                    <Text type="secondary">{data.user.email}</Text>
                                    {data.user.phone && (
                                        <Text type="secondary" className="ml-2">
                                            | {data.user.phone}
                                        </Text>
                                    )}
                                </div>
                                {data && (
                                    <Tooltip title="AI-powered troubleshooting for authentication and authorization issues">
                                        <Button
                                            type="secondary"
                                            size="small"
                                            icon={<Sparkles />}
                                            onClick={() => setTroubleshootingOpen(true)}
                                        >
                                            AI Troubleshoot
                                        </Button>
                                    </Tooltip>
                                )}
                            </>
                        )}
                    </div>
                }
                styles={{ wrapper: { width: '70vw' } }}
                open={open}
                onClose={() => setOpen(false)}
                afterOpenChange={(isOpen) => {
                    if (!isOpen) {
                        navigate({ to: '/admin-console/user-access/users' });
                    }
                }}
                destroyOnHidden
            >
                <TRPCErrorBoundary error={error}>
                    {isLoading && (
                        <div className="flex h-96 items-center justify-center">
                            <Spin size="large" tip="Loading user details..." />
                        </div>
                    )}

                    {error && (
                        <Alert
                            title="Error loading user details"
                            description="Please try again or contact support if the problem persists."
                            type="error"
                            showIcon
                        />
                    )}

                    {data && (
                        <>
                            {/* User Journey Alert if there are issues */}
                            {data.journeySummary?.riskFactors && data.journeySummary.riskFactors.length > 0 && (
                                <Alert
                                    title={data.journeySummary.headline}
                                    description={
                                        <ul className="mt-2 mb-0 pl-4">
                                            {data.journeySummary.riskFactors.map((risk, index) => (
                                                <li key={index}>{risk}</li>
                                            ))}
                                        </ul>
                                    }
                                    type="warning"
                                    showIcon
                                    className="mb-4"
                                />
                            )}

                            {/* Tabs */}
                            <Tabs defaultActiveKey="authentication" items={tabItems} />
                        </>
                    )}
                </TRPCErrorBoundary>

                {/* Troubleshooting Drawer */}
                <TroubleshootingDrawer
                    userId={userId}
                    open={troubleshootingOpen}
                    onClose={() => setTroubleshootingOpen(false)}
                />
            </Drawer>
        </PageController>
    );
}
